import { useEffect, useState } from "react";
import { MoonStar, RotateCcw, Package, RefreshCw, Anchor } from "lucide-react";

const NIGHT_FACTS = [
    { icon: Package, value: "100 kg / 1 m³", label: "One Guardian's hold per run" },
    { icon: RefreshCw, value: "~3 runs a night", label: "Including the trip back to the pod to offload and redeploy" },
    { icon: Anchor, value: "Up to 2.7 t", label: "A full nine-Guardian fleet at capacity in a single night" },
];

export default function NightSift() {
    const [kg, setKg] = useState(96.3);
    const [target, setTarget] = useState(486);
    const done = kg >= target;

    useEffect(() => {
        if (done) return;
        const id = setInterval(() => {
            setKg((v) => Math.min(v + 0.5 + Math.random() * 1.1, target));
        }, 1400);
        return () => clearInterval(id);
    }, [done, target]);

    const reset = () => {
        setKg(0);
        setTarget(Math.round(420 + Math.random() * 140));
    };

    const pct = Math.min((kg / target) * 100, 100);

    return (
        <div className="bg-[#06182A] border border-[#12283C] rounded-xl p-7" data-testid="night-sift-widget">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <MoonStar className="w-5 h-5 text-sky-200" />
                    <p className="font-display font-semibold text-lg" data-testid="night-sift-title">
                        Last night's sift, Meadowbank pod
                    </p>
                </div>
                <span className="text-sm px-3 py-1 rounded-full border border-sky-400/40 text-sky-200" data-testid="night-sift-badge">
                    Example, demonstration data, resets nightly
                </span>
            </div>

            <div className="mt-6 flex items-end gap-3">
                <p className="font-display font-semibold text-6xl text-sky-200 leading-none" data-testid="night-sift-value">
                    {kg.toFixed(1)}
                </p>
                <p className="text-xl text-slate-400 pb-1">kg collected</p>
            </div>

            <div className="mt-5 h-3 rounded-full bg-[#12283C] overflow-hidden" data-testid="night-sift-progress">
                <div
                    className="h-full rounded-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-[width] duration-700"
                    style={{ width: `${pct}%` }}
                />
            </div>

            <p className="mt-4 text-base text-slate-400 leading-relaxed" data-testid="night-sift-status">
                {done
                    ? `Night complete, after last night's sift the fleet picked up ${target} kg of waste before returning to the pod at 6:30am.`
                    : `Guardians are mid-sift… rolling toward tonight's haul of ${target} kg across the Meadowbank reach, counting every trip back to the pod to offload, turn around and redeploy.`}
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#12283C] border border-[#12283C] rounded-xl overflow-hidden" data-testid="night-sift-facts">
                {NIGHT_FACTS.map((fact, i) => {
                    const Icon = fact.icon;
                    return (
                        <div key={fact.value} className="bg-[#041222] p-4" data-testid={`night-sift-fact-${i}`}>
                            <Icon className="w-4 h-4 text-sky-200" />
                            <p className="mt-2 font-display font-semibold text-lg text-sky-200">{fact.value}</p>
                            <p className="mt-1 text-xs text-slate-400 leading-snug">{fact.label}</p>
                        </div>
                    );
                })}
            </div>

            <button
                data-testid="night-sift-reset-button"
                onClick={reset}
                className="mt-5 inline-flex items-center gap-2 border border-sky-400/40 hover:border-sky-200 text-sky-200 font-semibold text-sm px-5 py-2.5 rounded-full transition-colors"
            >
                <RotateCcw className="w-4 h-4" />
                Roll over, start a new night
            </button>
        </div>
    );
}
